import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { listServicesApi, updateService, uploadUrl } from '../lib/api';
import { ArrowLeft, Save, Image as ImageIcon } from 'lucide-react';

export default function ServicesEditor({ onOpenMedia }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      setItems(await listServicesApi());
    } catch {}
  };
  useEffect(() => { load(); }, []);

  if (editing) {
    return <ServiceForm service={editing} onBack={() => { setEditing(null); load(); }} onOpenMedia={onOpenMedia} />;
  }

  return (
    <div className="bg-white border border-aqc-line rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-2xl text-aqc-navy">Services</h2>
        <span className="text-xs text-aqc-muted">{items.length} services</span>
      </div>
      <p className="text-sm text-aqc-muted mb-5">Edit title, tagline, bullets, deliverables and image for each core service.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((s) => (
          <button key={s.slug} onClick={() => setEditing(s)} className="text-left flex gap-4 bg-aqc-cream/60 hover:bg-aqc-cream border border-aqc-line rounded-xl p-4 transition-colors">
            <img src={s.image} alt={s.title} className="w-24 h-16 object-cover rounded-md" />
            <div className="flex-1 min-w-0">
              <div className="font-serif text-lg text-aqc-navy">{s.title}</div>
              <div className="text-xs text-aqc-muted mt-0.5 line-clamp-2">{s.tagline}</div>
              <div className="mt-1.5 text-[0.7rem] uppercase tracking-[0.14em] text-aqc-red font-semibold">Edit →</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ServiceForm({ service, onBack, onOpenMedia }) {
  const [f, setF] = useState({
    title: service.title || '',
    tagline: service.tagline || '',
    icon: service.icon || '',
    image: service.image || '',
    bullets: (service.bullets || []).join('\n'),
    deliverables: (service.deliverables || []).join('\n'),
    order: service.order || 0,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await updateService(service.slug, {
        title: f.title,
        tagline: f.tagline,
        icon: f.icon,
        image: f.image,
        bullets: f.bullets.split('\n').map((x) => x.trim()).filter(Boolean),
        deliverables: f.deliverables.split('\n').map((x) => x.trim()).filter(Boolean),
        order: Number(f.order) || 0,
      });
      toast({ title: 'Service saved' });
      onBack();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-aqc-line rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-aqc-navy"><ArrowLeft size={14} /> Back to services</button>
        <span className="text-xs text-aqc-muted font-mono">/{service.slug}</span>
      </div>
      <h2 className="font-serif text-2xl text-aqc-navy">Edit — {service.title}</h2>
      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1.5" /></div>
        <div><Label>Icon (lucide-react name)</Label><Input value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} className="mt-1.5" placeholder="ClipboardCheck | FlaskConical | ShieldCheck | Ship" /></div>
        <div className="sm:col-span-2"><Label>Tagline</Label><Input value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} className="mt-1.5" /></div>
        <div className="sm:col-span-2">
          <Label>Image URL</Label>
          <div className="flex gap-2 mt-1.5">
            <Input value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} placeholder="https://... or /api/uploads/..." />
            <Button type="button" variant="outline" onClick={() => onOpenMedia && onOpenMedia((url) => setF((s) => ({ ...s, image: url })))} className="whitespace-nowrap gap-1.5"><ImageIcon size={14} /> Pick</Button>
          </div>
          {f.image && <img src={uploadUrl(f.image)} alt="preview" className="mt-3 w-48 h-28 object-cover rounded-md border border-aqc-line" />}
        </div>
        <div><Label>Order</Label><Input type="number" value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} className="mt-1.5" /></div>
        <div />
        <div className="sm:col-span-2">
          <Label>Scope bullets (one per line)</Label>
          <Textarea rows={6} value={f.bullets} onChange={(e) => setF({ ...f, bullets: e.target.value })} className="mt-1.5" />
        </div>
        <div className="sm:col-span-2">
          <Label>Deliverables (one per line)</Label>
          <Textarea rows={5} value={f.deliverables} onChange={(e) => setF({ ...f, deliverables: e.target.value })} className="mt-1.5" />
        </div>
      </div>
      <div className="mt-6 flex gap-2">
        <Button onClick={save} disabled={saving} className="bg-aqc-navy hover:bg-[hsl(var(--aqc-navy-2))] text-white gap-1.5"><Save size={14} /> {saving ? 'Saving…' : 'Save changes'}</Button>
        <Button variant="outline" onClick={onBack}>Cancel</Button>
      </div>
    </div>
  );
}
